/**
 * Hook for constructing the tables
 */
import React from 'react';

import {
  Column,
  useFilters,
  useGroupBy,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import { Action } from '~shared/components/ActionMenu/ActionMenu';
import {
  alphanumeric,
  naturalLibraryName,
} from '~shared/components/Table/sortingFns';

/**
 * Table custom hook that extends the useTable hook with the customizations
 * This hook is what you use to create a table instance.
 * It comes ready to permit various abilities, such as search, paginate, increment, sorting, and more.
 */

interface SortingRule<D> {
  id: keyof D & string;
  desc: boolean;
}
interface UseSocTableParams<D extends object> {
  filters?: ((data: D[], search?: string) => D[])[];
  data: (D & { disabled?: boolean })[];
  columns?: ReadonlyArray<Column<D>>;
  enableBulkActions?: boolean;
  staticSort?: (a, b) => number;
  increment?: number;
  searchQuery?: { id: string; value: string };
  actions?: Action<D>[];
  sortable?: boolean;
  actionColumn?: {
    header: (props) => React.ReactNode;
    cell: (props) => React.ReactNode;
  };
  selectionColumn?: {
    header: (props) => React.ReactNode;
    cell: (props) => React.ReactNode;
  };
  initialSort?: SortingRule<D>;
}
export const useSocTable = <D extends object>({
  filters,
  data,
  columns,
  enableBulkActions,
  increment,
  searchQuery,
  actions,
  sortable,
  actionColumn,
  selectionColumn,
  staticSort,
  initialSort,
}: UseSocTableParams<D>) => {
  // 2. initialize any filters
  const filter: (D & { disabled?: boolean })[] = React.useMemo(() => {
    // we are going to want to first apply the disabled prop to our data
    const mutatedData = data.map((obj) => ({
      ...obj,
      disabled: false,
    }));
    if (!filters) return mutatedData;
    return filters.reduce(
      (mutatedData, filter) => filter(mutatedData),
      mutatedData
    );
  }, [data, filters]);

  const customInitialState = () => {
    return {
      ...(initialSort && { sortBy: [initialSort] }),
    };
  };

  // 4. bring in the useTable hook from react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    setFilter,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected,
    dispatch,
    state,
  } = useTable<D>(
    {
      columns,
      initialState: customInitialState(),
      sortTypes: {
        // custom sort that does not take case into account
        alphanumeric,
        naturalLibraryName,
      },
      data: filter || ([] as ReadonlyArray<D>),
      disableSortBy: !sortable,
      stateReducer: (newState, action) => {
        switch (action.type) {
          case 'action-update':
            return {
              ...newState,
              actions: action.payload,
            };
        }
      },
    },
    useFilters,
    useGroupBy,
    useSortBy,
    useRowSelect,
    (hooks) => {
      // here we handle the custom selection column, and the custom ellipsis column
      enableBulkActions && actions && selectionColumn
        ? hooks.visibleColumns.push((columns) => [
            // using render props to allow for external react component for header and cells
            // essentially you use this to determine EXACTLY what your cells and header look like
            {
              id: 'selection',
              customWidth: '60px',
              doNotShowSort: true,
              Header: ({ getToggleAllRowsSelectedProps }) =>
                selectionColumn.header(getToggleAllRowsSelectedProps),
              Cell: ({ row }) => selectionColumn.cell({ row }),
            },
            ...columns,
          ])
        : null;
      actions && actionColumn
        ? hooks.visibleColumns.push((columns) => [
            ...columns,
            {
              id: 'actions',
              customWidth: '60px',
              doNotShowSort: true,
              Header: ({ selectedFlatRows, state }) =>
                actionColumn.header({
                  selectedRows: selectedFlatRows,
                  actions: state.actions,
                  enableBulkActions,
                }),
              Cell: ({ row, state }) =>
                actionColumn.cell({ row, actions: state.actions }),
            },
          ])
        : null;
    }
  );

  // when new items are selected, make sure that the actions are being updated
  React.useEffect(() => {
    dispatch({ type: 'action-update', payload: actions });
  }, [actions, dispatch]);

  // handle any searches
  React.useEffect(() => {
    if (!searchQuery) return;
    const { id, value } = searchQuery;
    if (!id) throw new Error('Column search is missing an id to follow');
    setFilter(id, value);
  }, [setFilter, searchQuery]);

  // handle the max rows
  const [maxRows, setMaxRows] = React.useState(increment);

  // method to increase the number of rows
  const incrementFn = React.useCallback(() => {
    setMaxRows((state) => state + increment);
  }, [increment]);

  // return all things that are needed to construct a table
  return React.useMemo(
    () => ({
      resolvedData: rows.sort(staticSort).slice(0, maxRows),
      totalRows: rows.length,
      incrementFn,
      prepareRow,
      maxRows,
      selectedRows: selectedFlatRows,
      toggleAllRows: toggleAllRowsSelected,
      state: {
        currentSort: state.sortBy,
      },
      props: {
        getTableProps,
        getTableBodyProps,
        headerGroups,
      },
    }),
    [
      rows,
      staticSort,
      maxRows,
      incrementFn,
      prepareRow,
      selectedFlatRows,
      toggleAllRowsSelected,
      state.sortBy,
      getTableProps,
      getTableBodyProps,
      headerGroups,
    ]
  );
};
